import React, { useCallback, useEffect, useMemo, useState } from "react";
import CustomSelect from "./ui/CustomSelect";
import { getSymbolInfo } from "../api/api";
import { listSymbols, searchSymbols } from "../api/dataWarehouseApi";

function normalizeSymbolValue(value) {
    return String(value ?? "").trim();
}

function getWatchlistSymbol(item) {
    return normalizeSymbolValue(item?.instrument ?? item?.symbol ?? item?.instrument_symbol);
}

function getWatchlistExchange(item) {
    return normalizeSymbolValue(item?.exchange ?? item?.market ?? item?.asset_class);
}

function getSymbolLabel(item, symbol) {
    const name = normalizeSymbolValue(item?.name ?? item?.description);
    const exchange = getWatchlistExchange(item);
    const assetClass = normalizeSymbolValue(item?.asset_class);

    if (name && name.toUpperCase() !== symbol.toUpperCase()) {
        return `${symbol} - ${name}`;
    }
    if (exchange) return `${symbol} (${exchange})`;
    if (assetClass) return `${symbol} (${assetClass})`;
    return symbol;
}

function getErrorMessage(error) {
    return String(error?.message || error?.detail || error?.data?.detail || "");
}

const SymbolSelection = ({
    selectedSymbol,
    setSelectedSymbol,
    watchlist,
    symbolCatalog = [],
    accountId,
    label = "Symbol",
    showLabel = true,
    compact = false,
    className = "",
    selectClassName = "",
    buttonClassName = "",
}) => {
    const [error, setError] = useState(null);
    const [symbolSearchQuery, setSymbolSearchQuery] = useState("");
    const [remoteSymbolMatches, setRemoteSymbolMatches] = useState([]);
    const [isSearchingSymbols, setIsSearchingSymbols] = useState(false);

    const handleSearchQueryChange = useCallback((query) => {
        setSymbolSearchQuery(query);
    }, []);

    useEffect(() => {
        const query = symbolSearchQuery.trim();
        if (query.length < 2) {
            setRemoteSymbolMatches([]);
            setIsSearchingSymbols(false);
            return undefined;
        }

        let cancelled = false;
        setIsSearchingSymbols(true);

        const timer = setTimeout(async () => {
            try {
                let items = [];

                try {
                    const response = await searchSymbols(query);
                    items = Array.isArray(response?.results)
                        ? response.results
                        : (Array.isArray(response) ? response : []);
                } catch {
                    items = [];
                }

                if (items.length === 0) {
                    const response = await listSymbols({ search: query, page: 1, page_size: 20 });
                    items = Array.isArray(response?.results)
                        ? response.results
                        : (Array.isArray(response) ? response : []);
                }

                if (!cancelled) {
                    setRemoteSymbolMatches(items);
                }
            } catch (err) {
                if (!cancelled) {
                    setRemoteSymbolMatches([]);
                }
            } finally {
                if (!cancelled) {
                    setIsSearchingSymbols(false);
                }
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [symbolSearchQuery]);

    const options = useMemo(() => {
        const nextOptions = [];
        const seen = new Set();

        const appendOption = (item, searchText = "") => {
            const symbol = getWatchlistSymbol(item);
            if (!symbol) return;

            const key = symbol.toUpperCase();
            if (seen.has(key)) return;

            seen.add(key);
            nextOptions.push({
                value: symbol,
                label: getSymbolLabel(item, symbol),
                searchText,
            });
        };

        const currentSearchText = symbolSearchQuery.trim();
        (Array.isArray(remoteSymbolMatches) ? remoteSymbolMatches : []).forEach((item) => appendOption(item, currentSearchText));
        (Array.isArray(symbolCatalog) ? symbolCatalog : []).forEach(appendOption);
        (Array.isArray(watchlist) ? watchlist : []).forEach(appendOption);

        const currentSymbol = normalizeSymbolValue(selectedSymbol);
        if (currentSymbol && !seen.has(currentSymbol.toUpperCase())) {
            nextOptions.unshift({
                value: currentSymbol,
                label: currentSymbol,
            });
        }

        return nextOptions;
    }, [watchlist, symbolCatalog, remoteSymbolMatches, selectedSymbol, symbolSearchQuery]);

    // Clear stale error when account becomes available
    useEffect(() => {
        if (accountId !== null && accountId !== undefined && accountId !== '') {
            setError(null);
        }
    }, [accountId]);

    const handleSymbolChange = async (newSymbol) => {
        if (!newSymbol || newSymbol === selectedSymbol) return;

        if (accountId === null || accountId === undefined || accountId === '') {
            setError("Please select an account first.");
            return;
        }
        setError(null); // Clear previous errors

        try {
            await getSymbolInfo(accountId, newSymbol);
            setSelectedSymbol(newSymbol); // Update global state only if validation succeeds
        } catch (err) {
            const message = getErrorMessage(err).toLowerCase();
            if (message.includes("not found")) {
                setError(`Symbol "${newSymbol}" not found on this platform.`);
            } else {
                setError("Could not validate this symbol.");
            }
            console.error("Symbol validation error:", err);
        }
    };

    const labelClasses = compact
        ? "shrink-0 text-[10px] font-medium uppercase tracking-[0.08em] text-textSecondary"
        : "shrink-0 text-lg font-semibold";
    const wrapperClasses = compact
        ? "text-textPrimary flex min-w-0 flex-col gap-1"
        : "text-textPrimary flex flex-col items-start space-y-1";
    const rowClasses = compact
        ? "flex min-w-0 items-center gap-2"
        : "flex items-center space-x-2";

    return (
        <div className={`${wrapperClasses} ${className}`}>
            <div className={rowClasses}>
                {showLabel && <span className={labelClasses}>{label}</span>}
                <CustomSelect
                    options={options}
                    value={selectedSymbol || ""}
                    onChange={handleSymbolChange}
                    placeholder={options.length > 0 ? "Select symbol" : "No symbols"}
                    searchable
                    searchPlaceholder="Search symbols..."
                    virtualize
                    maxDropdownHeight={300}
                    itemHeight={34}
                    dropdownMinWidth={220}
                    emptyText={isSearchingSymbols ? "Searching symbols..." : "No results"}
                    onSearchQueryChange={handleSearchQueryChange}
                    className={`${compact ? "min-w-[150px] flex-1" : "min-w-[190px]"} ${selectClassName}`}
                    buttonClassName={`${compact ? "py-1.5 pl-2 pr-8 text-xs sm:text-xs" : ""} ${buttonClassName}`}
                    optionPaddingLeft={28}
                    renderSelected={(option) => option.value}
                />
            </div>
            {error && <p className="text-red-500 text-xs ml-2">{error}</p>}
        </div>
    );
};

export default SymbolSelection;
